// src/features/calendar/CalendarMatrixIndex.jsx

import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Paper,
    Tooltip,
    Typography,
    useTheme,
    TextField,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    MenuItem,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import dayjs from "dayjs";
import { tokens } from "../../assets/theme";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useCalendarMatrix } from "../../hooks/useCalendarMatrix";
import { fetchMachineList } from "../../fetchers/machine";
import {
    useWeekTemplates,
    useAssignTemplate,
    useRemoveAssignedTemplate
} from "../../hooks/useWeekSchedule";
import { useOverrideDay, useClearOverrideDay } from "../../hooks/useProductionCalendar";
import {
    pushNotification,
    pushErrorMessage,
} from "../../utils/notifications";
import messages from "./components/messages";
import { FormattedMessage, useIntl } from "react-intl";

function CalendarMatrixIndex() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const queryClient = useQueryClient();
    const { formatMessage } = useIntl();
    const [companyKey] = useLocalStorage("companyKey", 1);

    // Visa en månad i taget
    const [month, setMonth] = useState(dayjs().format("YYYY-MM"));

    const startDate = dayjs(month + "-01");
    const endDate = startDate.endOf("month");
    const daysInMonth = endDate.date();

    const { data: machines = [] } = useQuery({
        queryKey: ["machinelist", companyKey],
        queryFn: () => fetchMachineList(companyKey),
        staleTime: 60000,
        refetchInterval: 60000,
    });

    const { data: calendar = [] } = useCalendarMatrix(
        companyKey,
        startDate.format("YYYY-MM-DD"),
        endDate.format("YYYY-MM-DD")
    );

    const { data: templates = [] } = useWeekTemplates(companyKey);
    const assignTemplateMutation = useAssignTemplate();
    const removeAssignedTemplateMutation = useRemoveAssignedTemplate();
    const overrideMutation = useOverrideDay();
    const clearOverrideMutation = useClearOverrideDay();

    // Snabb-lookup för cellvärden
    const cellMap = useMemo(() => {
        const map = {};
        calendar?.forEach((row) => {
            const date = dayjs(row.date);
            const key = `${row.machineId}_${date.format("YYYY-MM-DD")}`;
            map[key] = row;
        });
        return map;
    }, [calendar]);

    const dayColumnWidth = 42;

    // --- Dialog-state för dubbelklickad cell ---
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedCell, setSelectedCell] = useState(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [overrideEnabled, setOverrideEnabled] = useState(false);
    const [overrideHours, setOverrideHours] = useState("");

    const handleCellDoubleClick = (machine, dateStr, entry) => {
        setSelectedCell({ machine, dateStr, entry });

        // Förifyll schema
        setSelectedTemplateId(entry?.weekTemplateId ?? "");

        // Förifyll override om dagen redan är överstyrd
        if (entry?.isOverridden && entry?.plannedProductionHours != null) {
            setOverrideEnabled(true);
            setOverrideHours(entry.plannedProductionHours.toString());
        } else {
            setOverrideEnabled(false);
            setOverrideHours("");
        }

        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedCell(null);
        setSelectedTemplateId("");
        setOverrideEnabled(false);
        setOverrideHours("");
    };

    const handleDialogSave = async () => {
        if (!selectedCell) return;

        const { machine, dateStr, entry } = selectedCell;
        const machineId = machine.id;
        const fromIso = dayjs(dateStr).startOf("day").toISOString();
        const toIso = dayjs(dateStr).endOf("day").toISOString();
        const dateOnly = dayjs(dateStr).format("YYYY-MM-DD");

        try {

            if (!overrideEnabled && entry?.isOverridden) {
                await clearOverrideMutation.mutateAsync({ machineId, date: dayjs(dateStr).toISOString() });
            }

            // 1) Override prioriteras om den är påslagen och vi har timmar
            if (overrideEnabled && overrideHours !== "") {
                const hoursNumber = Number(overrideHours.replace(",", "."));
                if (isNaN(hoursNumber) || hoursNumber < 0) {
                    pushErrorMessage(formatMessage(messages.errorInvalidNumberOfHours));
                    return;
                }

                await overrideMutation.mutateAsync({
                    machineId,
                    date: dayjs(dateStr).toISOString(),
                    plannedProductionHours: hoursNumber,
                });

            } else if (selectedTemplateId) {
                // 2) Annars ändrar vi schemat för just den dagen
                await assignTemplateMutation.mutateAsync({
                    machineId,
                    weekTemplateId: Number(selectedTemplateId),
                    validFrom: fromIso,
                    validTo: toIso,
                });
            } else {
                // Varken schema eller override -> gör inget
                pushErrorMessage(formatMessage(messages.errorNoTemplateOrHoursProvided));
                return;
            }

            // Uppdatera matrisen
            await queryClient.invalidateQueries({
                queryKey: ["calendarMatrix"],
                exact: false,
            });

            handleDialogClose();
        } catch (err) {
            console.error(err);
            pushErrorMessage(formatMessage(messages.errorCouldNotUpdateScheduleForDay));
        }
    };

    return (
        <Box p={2}>
            <Typography variant="h5" mb={2}>
                <FormattedMessage {...messages.scheduleOverview} />
            </Typography>

            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} md={2}>
                    <TextField
                        label={<FormattedMessage {...messages.month} />}
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            '& input::-webkit-calendar-picker-indicator': {
                                filter: 'invert(1)',
                                opacity: 0.8,
                            },
                        }}
                    />
                </Grid>
                <Grid item 
                    xs={12} 
                    md="auto"
                    sx={{ 
                        ml: "auto",
                        display: "flex",
                        justifyContent: "flex-end",
                    }}>
                    <Paper
                        sx={{
                            mb: 2,
                            px: 2,
                            py: 1.5,
                            backgroundColor: colors.primary[500],
                            border: `1px solid ${colors.primary[600]}`,
                            textAlign: "right",
                            display: "inline-flex",
                            flexDirection: "column",
                            width: "fit-content",
                            maxWidth: "min(100%, 600px)",
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            <FormattedMessage {...messages.legendTitle} />
                        </Typography>

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                columnGap: 2,
                                rowGap: 1,
                                alignItems: "center",
                                justifyContent: "flex-end"
                            }}
                        >
                            {templates.map((t) => (
                                <Box
                                    key={t.id}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 18,
                                            backgroundColor: t.templateColor || "#888",
                                            border: `1px solid ${colors.primary[600]}`,
                                        }}
                                    />
                                    <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                                        {t.name}
                                    </Typography>
                                </Box>
                            ))}

                            <Box sx={{ flexBasis: "100%", height: 0 }} />

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 28,
                                        height: 18,
                                        backgroundColor: "#555",
                                        backgroundImage: `repeating-linear-gradient(
                                            135deg,
                                            rgba(137, 137, 137, 0) 0px,
                                            rgba(137, 137, 137, 0) 6px,
                                            rgba(181, 181, 181, 1) 6px,
                                            rgba(181, 181, 181, 1) 12px
                                        )`,
                                        backgroundBlendMode: "screen",
                                        border: "1px solid rgba(181, 181, 181, 0.8)",
                                        borderRadius: "2px",
                                    }}
                                />
                                <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                                    <FormattedMessage {...messages.holidayLegend} />
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 28,
                                        height: 18,
                                        backgroundColor: "#3F4451",
                                        border: "2px solid #ffc107",
                                    }}
                                />
                                <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                                    <FormattedMessage {...messages.manuallyOverriddenLegend} />
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 28,
                                        height: 18,
                                        backgroundColor: "#3F4451",
                                        border: `1px solid ${colors.primary[600]}`,
                                    }}
                                />
                                <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                                    <FormattedMessage {...messages.unplannedLegend} />
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table
                    sx={{
                        minWidth: 650,
                        "& th": {
                            backgroundColor: colors.primary[600],
                            color: "#fff",
                            fontSize: "0.9rem",
                        },
                        "& td": {
                            backgroundColor: colors.primary[400],
                            borderColor: colors.primary[600],
                            color: "#fff",
                            fontSize: "0.85rem",
                            padding: "6px",
                            textAlign: "center",
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 2,
                                    backgroundColor: colors.primary[600],
                                    fontWeight: 600,
                                    minWidth: 180,
                                    maxWidth: 180,
                                    width: 180,
                                    textAlign: "left",
                                    paddingLeft: "16px !important",
                                }}
                            >
                                <FormattedMessage {...messages.machine} />
                            </TableCell>
                            {Array.from({ length: daysInMonth }).map((_, i) => (
                                <TableCell
                                    key={i + 1}
                                    align="center"
                                    sx={{
                                        minWidth: dayColumnWidth,
                                        maxWidth: dayColumnWidth,
                                        width: dayColumnWidth,
                                        padding: "2px",
                                        fontSize: "0.75rem",
                                        borderLeft: `1px solid ${colors.primary[700]}`,
                                        borderBottom: `1px solid ${colors.primary[700]}`,
                                    }}
                                >
                                    {i + 1}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {machines.map((m) => (
                            <TableRow key={m.id}>
                                <TableCell
                                    sx={{
                                        paddingLeft: "16px !important",
                                        fontWeight: 600,
                                        backgroundColor: colors.primary[500],
                                        textAlign: "left !important",
                                    }}
                                >
                                    {m.machineName}
                                </TableCell>
                                {Array.from({ length: daysInMonth }).map((_, i) => {
                                    const dateStr = startDate
                                        .date(i + 1)
                                        .format("YYYY-MM-DD");
                                    const key = `${m.id}_${dateStr}`;
                                    const entry = cellMap[key];
                                    const isHoliday = entry?.source === 2;

                                    // Tom cell (inget schema/entry)
                                    if (!entry) {
                                        return (
                                            <TableCell
                                                key={i}
                                                onDoubleClick={() =>
                                                    handleCellDoubleClick(
                                                        m,
                                                        dateStr,
                                                        null
                                                    )
                                                }
                                                sx={{ cursor: "pointer" }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                        height: "24px",
                                                        backgroundColor:
                                                            "rgba(255,255,255,0.05)",
                                                    }}
                                                />
                                            </TableCell>
                                        );
                                    }

                                    // Cell med schema
                                    return (
                                        <Tooltip
                                            key={i}
                                            title={
                                                <Box p={1}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontSize: "1rem",
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {entry.source === 2 ? "Helgdag" : entry.weekTemplateName}
                                                    </Typography>
                                                    {entry.source === 0 && entry.shifts.map(
                                                        (s, idx) => (
                                                            <Typography
                                                                variant="body2"
                                                                key={idx}
                                                                sx={{
                                                                    fontSize:
                                                                        "0.95rem",
                                                                }}
                                                            >
                                                                {s.startTime.substring(0, 5)}{" "}-{" "}{s.endTime.substring(0, 5)}{" "}({s.label})
                                                            </Typography>
                                                        )
                                                    )}
                                                    {entry.isOverridden && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize:
                                                                    "0.85rem",
                                                                color: "#ffc107",
                                                            }}
                                                        >
                                                            <FormattedMessage {...messages.overriddenHours} /> ({entry.plannedProductionHours} <FormattedMessage {...messages.hours} />)
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                        >
                                            <TableCell
                                                onDoubleClick={() =>
                                                    handleCellDoubleClick(
                                                        m,
                                                        dateStr,
                                                        entry
                                                    )
                                                }
                                                sx={{ cursor: "pointer" }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                        height: "24px",

                                                        // Basfärg
                                                        backgroundColor: isHoliday
                                                            ? "#555"
                                                            : entry.templateColor ?? "#555",

                                                        // Diagonalt mönster för helgdag
                                                        backgroundImage: isHoliday
                                                            ? `repeating-linear-gradient(
                                                                135deg,
                                                                rgba(137, 137, 137, 0) 0px,
                                                                rgba(137, 137, 137, 0) 6px,
                                                                rgba(181, 181, 181, 1) 6px,
                                                                rgba(181, 181, 181, 1) 12px
                                                                )`
                                                            : "none",

                                                        backgroundBlendMode: isHoliday ? "screen" : "normal",

                                                        borderRadius: entry.isOverridden ? "2px" : 0,
                                                        opacity: entry.isOverridden ? 0.9 : 1,

                                                        border: isHoliday
                                                            ? "1px solid rgba(181, 181, 181, 0.8)"
                                                            : entry.isOverridden
                                                                ? "2px solid #ffc107"
                                                                : "1px solid transparent",
                                                    }}
                                                />
                                            </TableCell>
                                        </Tooltip>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: colors.primary[500],
                        color: "#ffffff",
                        border: `1px solid ${colors.primary[600]}`,
                    },
                }}
            >
                <DialogTitle><FormattedMessage {...messages.changeOrOverrideTemplatee} /></DialogTitle>
                <DialogContent dividers>
                    {selectedCell && (
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <Typography variant="body2">
                                <strong><FormattedMessage {...messages.machine} />:</strong>{" "}
                                {selectedCell.machine.machineName}
                            </Typography>
                            <Typography variant="body2">
                                <strong><FormattedMessage {...messages.date} />:</strong>{" "}
                                {dayjs(selectedCell.dateStr).format(
                                    "YYYY-MM-DD"
                                )}
                            </Typography>

                            {/* Välj schema för just den här dagen */}
                            <TextField
                                select
                                fullWidth
                                label={<FormattedMessage {...messages.selectWeekTemplate} />}
                                value={selectedTemplateId || ""}
                                onChange={(e) =>
                                    setSelectedTemplateId(
                                        e.target.value === ""
                                            ? ""
                                            : Number(e.target.value)
                                    )
                                }
                                size="small"
                                margin="normal"
                            >
                                {templates.map((t) => (
                                    <MenuItem key={t.id} value={t.id}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 14,
                                                    height: 14,
                                                    borderRadius: "50%",
                                                    backgroundColor:
                                                        t.templateColor || "#888",
                                                    border:
                                                        "1px solid rgba(0,0,0,0.4)",
                                                }}
                                            />
                                            <span>{t.name}</span>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </TextField>

                            {/* Override */}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={overrideEnabled}
                                        onChange={(e) =>
                                            setOverrideEnabled(e.target.checked)
                                        }
                                    />
                                }
                                label={<FormattedMessage {...messages.overrideTemplateForDay} />}
                                sx={{ mt: 1 }}
                            />

                            <TextField
                                label={<FormattedMessage {...messages.nrOfHours} />}
                                type="number"
                                size="small"
                                fullWidth
                                disabled={!overrideEnabled}
                                value={overrideHours}
                                onChange={(e) => setOverrideHours(e.target.value)}
                                inputProps={{ min: 0, step: 0.5 }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={handleDialogClose}
                    >
                        <FormattedMessage {...messages.cancel} />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            if (!selectedCell) return;

                            const { machine, dateStr } = selectedCell;
                            const machineId = machine.id;
                            const dateOnly = dayjs(dateStr).format("YYYY-MM-DD");

                            try {
                                await removeAssignedTemplateMutation.mutateAsync({
                                    machineId,
                                    fromDate: dateOnly,
                                    toDate: dateOnly,
                                });

                                await queryClient.invalidateQueries({ queryKey: ["calendarMatrix"], exact: false });
                                handleDialogClose();
                            } catch (err) {
                                console.error(err);
                                pushErrorMessage(formatMessage(messages.errorCouldNotRemoveTemplateAssignment));
                            }
                        }}
                        disabled={removeAssignedTemplateMutation.isPending}
                    >
                        <FormattedMessage {...messages.removeTempalteForDay} />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDialogSave}
                        disabled={
                            assignTemplateMutation.isPending ||
                            overrideMutation.isPending
                        }
                    >
                        <FormattedMessage {...messages.save} />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default CalendarMatrixIndex;